import React, { useMemo } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { markSeoFixDetailTourCompleted } from '../utils/seoFixDetailTourStorage';

export { hasCompletedSeoFixDetailTour, markSeoFixDetailTourCompleted } from '../utils/seoFixDetailTourStorage';

const tourText = {
    title: { fontSize: '1.2rem', fontWeight: 700, color: '#111827', marginBottom: '0.5rem', lineHeight: 1.35 },
    body: { fontSize: '1rem', lineHeight: 1.55, color: '#374151', marginBottom: 0 },
};

/** Guided tour for AI SEO Optimizer → Fix Issues detail page. */
export default function SeoFixDetailGuidedTour({ run, onClose, onExit, includeImagesStep = true }) {
    const { t, i18n } = useTranslation();

    const steps = useMemo(() => {
        const all = [
            {
                target: 'body',
                content: (
                    <div style={{ maxWidth: 480 }}>
                        <p className="mb-2 fw-bold" style={{ ...tourText.title, fontSize: '1.3rem' }}>
                            {t('seoFixDetailTour.welcomeTitle')}
                        </p>
                        <p style={tourText.body}>{t('seoFixDetailTour.welcomeBody')}</p>
                    </div>
                ),
                placement: 'center',
                disableBeacon: true,
            },
            {
                target: '#seo-detail-nav-bar',
                content: (
                    <div style={{ maxWidth: 420 }}>
                        <p style={tourText.title}>{t('seoFixDetailTour.navTitle')}</p>
                        <p style={tourText.body}>{t('seoFixDetailTour.navBody')}</p>
                    </div>
                ),
                disableBeacon: true,
            },
            {
                target: '#seo-detail-score-row',
                content: (
                    <div style={{ maxWidth: 420 }}>
                        <p style={tourText.title}>{t('seoFixDetailTour.scoreTitle')}</p>
                        <p style={tourText.body}>{t('seoFixDetailTour.scoreBody')}</p>
                    </div>
                ),
                disableBeacon: true,
            },
            {
                target: '#seo-detail-keywords-intro',
                content: (
                    <div style={{ maxWidth: 440 }}>
                        <p style={tourText.title}>{t('seoFixDetailTour.keywordsIntroTitle')}</p>
                        <p style={tourText.body}>{t('seoFixDetailTour.keywordsIntroBody')}</p>
                    </div>
                ),
                placement: 'auto',
                disableBeacon: true,
            },
            {
                target: '#seo-detail-keywords-ai-fix',
                content: (
                    <div style={{ maxWidth: 400 }}>
                        <p style={tourText.title}>{t('seoFixDetailTour.keywordsAiTitle')}</p>
                        <p style={tourText.body}>{t('seoFixDetailTour.keywordsAiBody')}</p>
                    </div>
                ),
                placement: 'bottom',
                disableBeacon: true,
            },
            {
                target: '#seo-detail-keywords-rank-track',
                content: (
                    <div style={{ maxWidth: 400 }}>
                        <p style={tourText.title}>{t('seoFixDetailTour.keywordsRankTitle')}</p>
                        <p style={tourText.body}>{t('seoFixDetailTour.keywordsRankBody')}</p>
                    </div>
                ),
                placement: 'bottom',
                disableBeacon: true,
            },
            {
                target: '#seo-detail-titles-meta-rules',
                content: (
                    <div style={{ maxWidth: 460 }}>
                        <p style={tourText.title}>{t('seoFixDetailTour.titlesMetaRulesTitle')}</p>
                        <p style={tourText.body}>{t('seoFixDetailTour.titlesMetaRulesBody')}</p>
                    </div>
                ),
                placement: 'auto',
                disableBeacon: true,
            },
            {
                target: '#seo-detail-titles-meta-edits',
                content: (
                    <div style={{ maxWidth: 460 }}>
                        <p style={tourText.title}>{t('seoFixDetailTour.titlesMetaEditsTitle')}</p>
                        <p style={tourText.body}>{t('seoFixDetailTour.titlesMetaEditsBody')}</p>
                    </div>
                ),
                placement: 'left',
                disableBeacon: true,
            },
            {
                target: '#seo-detail-content-section',
                content: (
                    <div style={{ maxWidth: 460 }}>
                        <p style={tourText.title}>{t('seoFixDetailTour.contentTitle')}</p>
                        <p style={tourText.body}>{t('seoFixDetailTour.contentBody')}</p>
                    </div>
                ),
                disableBeacon: true,
            },
            {
                target: '#seo-detail-images-section',
                content: (
                    <div style={{ maxWidth: 460 }}>
                        <p style={tourText.title}>{t('seoFixDetailTour.imagesTitle')}</p>
                        <p style={tourText.body}>{t('seoFixDetailTour.imagesBody')}</p>
                    </div>
                ),
                disableBeacon: true,
            },
            {
                target: 'body',
                content: (
                    <div style={{ maxWidth: 480 }}>
                        <p className="mb-2 fw-bold" style={{ ...tourText.title, fontSize: '1.3rem' }}>
                            {t('seoFixDetailTour.doneTitle')}
                        </p>
                        <p style={tourText.body}>{t('seoFixDetailTour.doneBody')}</p>
                    </div>
                ),
                placement: 'center',
                disableBeacon: true,
            },
        ];
        const filtered = includeImagesStep
            ? all
            : all.filter((s) => s.target !== '#seo-detail-images-section');
        return filtered;
    }, [t, i18n.language, includeImagesStep]);

    const handleCallback = (data) => {
        const { status } = data;
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            markSeoFixDetailTourCompleted();
            const reason = status === STATUS.FINISHED ? 'finished' : 'skipped';
            onClose();
            window.requestAnimationFrame(() => {
                if (onExit) onExit(reason);
            });
        }
    };

    return (
        <Joyride
            steps={steps}
            run={run}
            continuous
            showProgress
            showSkipButton
            scrollToFirstStep
            scrollOffset={100}
            disableScrolling={false}
            spotlightPadding={8}
            callback={handleCallback}
            styles={{
                options: {
                    zIndex: 11000,
                    primaryColor: '#0d6efd',
                    textColor: '#1f2937',
                    width: undefined,
                },
                tooltip: {
                    borderRadius: 14,
                    fontSize: 16,
                    padding: '18px 22px',
                    minWidth: 360,
                    maxWidth: 'min(520px, calc(100vw - 32px))',
                    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.18)',
                },
                tooltipContainer: {
                    maxWidth: 'min(520px, calc(100vw - 32px))',
                },
                tooltipContent: {
                    padding: '2px 0 6px',
                    fontSize: 16,
                    lineHeight: 1.5,
                },
                tooltipFooter: {
                    marginTop: 14,
                },
                buttonNext: {
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 15,
                    padding: '10px 18px',
                },
                buttonBack: { color: '#6b7280', fontSize: 15 },
                buttonSkip: { color: '#6b7280', fontSize: 14 },
                buttonClose: { height: 14, width: 14, padding: 8 },
            }}
            locale={{
                back: t('guidedTour.btnBack'),
                close: t('guidedTour.btnClose'),
                last: t('guidedTour.btnDone'),
                next: t('guidedTour.btnNext'),
                skip: t('guidedTour.btnSkip'),
            }}
        />
    );
}
